import { Banknote, Briefcase, Globe, Mail, ShoppingBag } from "lucide-react";
import { useState } from "react";
// import { Button } from "@/components/ui/button";

export default function AccountSpecificPassword({ setPassword }) {
    const [selectedAccount, setSelectedAccount] = useState("");
    const [rules, setRules] = useState({
        length: 12,
        upper: true,
        lower: true,
        number: true,
        symbol: true,
    });

    const accounts = {
        "Bank Account": {
            length: 16,
            upper: true,
            lower: true,
            number: true,
            symbol: true,
            icon: <Banknote className="w-5 h-5" />,
        },
        "Social Media": {
            length: 12,
            upper: true,
            lower: true,
            number: true,
            symbol: false,
            icon: <Globe className="w-5 h-5 " />,
        },
        "Email Account": {
            length: 14,
            upper: true,
            lower: true,
            number: true,
            symbol: true,
            icon: <Mail className="w-5 h-5 " />,
        },
        "E-commerce": {
            length: 12,
            upper: true,
            lower: true,
            number: true,
            symbol: true,
            icon: <ShoppingBag className="w-5 h-5" />,
        },
        "Work Account": {
            length: 18,
            upper: true,
            lower: true,
            number: true,
            symbol: true,
            icon: <Briefcase className="w-5 h-5" />,
        },
    };

    const generatePassword = (rules) => {
        const { length, upper, lower, number, symbol, passphrase } = rules;

        // ✅ Word list for passphrases
        const words = [
            "ocean", "river", "forest", "mountain", "sky", "star", "moon", "sun",
            "cloud", "stone", "fire", "earth", "wind", "dream", "light", "shadow",
            "time", "echo", "path", "mind", "code", "pixel", "wave", "note"
        ];

        // 🔹 Passphrase mode
        if (passphrase) {
            let phrase = [];
            for (let i = 0; i < 3; i++) {
                let word = words[Math.floor(Math.random() * words.length)];
                word = word.charAt(0).toUpperCase() + word.slice(1);
                phrase.push(word);
            }

            let finalPhrase = phrase.join(""); // e.g. "RiverStoneDream"

            // Add number if enabled
            if (number) {
                finalPhrase += Math.floor(Math.random() * 100);
            }

            // Add safe symbol if enabled
            if (symbol) {
                const safeSymbols = "!@#$%^&*()-_+=.?";
                finalPhrase += safeSymbols.charAt(Math.floor(Math.random() * safeSymbols.length));
            }

            setPassword(finalPhrase);
            return;
        }

        // 🔹 Character-based password mode
        const upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowerChars = "abcdefghijklmnopqrstuvwxyz";
        const numberChars = "0123456789";
        const safeSymbols = "!@#$%^&*()-_+=.?"; // ✅ safe set only

        let allChars = "";
        if (upper) allChars += upperChars;
        if (lower) allChars += lowerChars;
        if (number) allChars += numberChars;
        if (symbol) allChars += safeSymbols;

        if (!allChars) {
            setPassword("⚠️ Please select at least one option");
            return;
        }

        // First character should not be a symbol
        let firstCharSet = "";
        if (upper) firstCharSet += upperChars;
        if (lower) firstCharSet += lowerChars;
        if (number) firstCharSet += numberChars;

        if (!firstCharSet) firstCharSet = lowerChars;

        let password = "";
        password += firstCharSet.charAt(Math.floor(Math.random() * firstCharSet.length));

        // Rest of the password
        for (let i = 1; i < length; i++) {
            password += allChars.charAt(Math.floor(Math.random() * allChars.length));
        }

        setPassword(password);
    };







    const handleAccountSelect = (acc) => {
        setSelectedAccount(acc);
        setRules(accounts[acc]); // load default rules
        generatePassword(accounts[acc]);
    };

    return (
        <div className="bg-white shadow-md rounded-xl p-6 text-center">


<h1 className="text-xl font-semibold mb-4">Account-Specific Passwords</h1>


            <p className="text-gray-600 mb-6">
                Choose an account type & customize its password rules.
            </p>

            {/* Account Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
                {Object.keys(accounts).map((acc) => {
                    const isSelected = selectedAccount === acc;
                    return (
                        <button
                            key={acc}
                            onClick={() => handleAccountSelect(acc)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isSelected
                                ? "bg-cyan-600 text-white shadow-md"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                        >
                            <span
                                className={`${isSelected ? "text-white" : "text-cyan-600"
                                    }`}
                            >
                                {accounts[acc].icon}
                            </span>
                            <span>{acc}</span>
                        </button>
                    );
                })}
            </div>

            {/* Customization Checkboxes */}
            {selectedAccount && (
                <div className="text-left">
                    <h4 className="text-md font-semibold mb-3">
                        Customize {selectedAccount} Rules:
                    </h4>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {/* Uppercase */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={rules.upper}
                                onChange={(e) => setRules({ ...rules, upper: e.target.checked })}
                                className="hidden peer"
                            />
                            <span className="w-5 h-5 border-2 border-cyan-600 rounded-md flex items-center justify-center peer-checked:bg-cyan-600 transition-colors">
                                {rules.upper && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700">Uppercase Letters</span>
                        </label>

                        {/* Lowercase */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={rules.lower}
                                onChange={(e) => setRules({ ...rules, lower: e.target.checked })}
                                className="hidden peer"
                            />
                            <span className="w-5 h-5 border-2 border-cyan-600 rounded-md flex items-center justify-center peer-checked:bg-cyan-600 transition-colors">
                                {rules.lower && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700">Lowercase Letters</span>
                        </label>

                        {/* Numbers */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={rules.number}
                                onChange={(e) => setRules({ ...rules, number: e.target.checked })}
                                className="hidden peer"
                            />
                            <span className="w-5 h-5 border-2 border-cyan-600 rounded-md flex items-center justify-center peer-checked:bg-cyan-600 transition-colors">
                                {rules.number && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700">Numbers</span>
                        </label>

                        {/* Symbols */}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={rules.symbol}
                                onChange={(e) => setRules({ ...rules, symbol: e.target.checked })}
                                className="hidden peer"
                            />
                            <span className="w-5 h-5 border-2 border-cyan-600 rounded-md flex items-center justify-center peer-checked:bg-cyan-600 transition-colors">
                                {rules.symbol && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700">Symbols</span>
                        </label>

                        {/* Passphrase Option */}
                        <label className="flex items-center gap-2 cursor-pointer col-span-2">
                            <input
                                type="checkbox"
                                checked={rules.passphrase}
                                onChange={(e) => setRules({ ...rules, passphrase: e.target.checked })}
                                className="hidden peer"
                            />
                            <span className="w-5 h-5 border-2 border-cyan-600 rounded-md flex items-center justify-center peer-checked:bg-cyan-600 transition-colors">
                                {rules.passphrase && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-gray-700">Generate Passphrase (words instead of random chars)</span>
                        </label>

                        {/* Length */}
                        {!rules.passphrase && (
                            <label className="col-span-2 flex items-center gap-2">
                                Length:
                                <input
                                    type="number"
                                    min="6"
                                    max="32"
                                    value={rules.length}
                                    onChange={(e) => setRules({ ...rules, length: Number(e.target.value) })}
                                    className="w-20 border p-1 rounded"
                                />
                            </label>
                        )}
                    </div>


                    {/* Button */}
                    <div className="mt-4 flex justify-center">
                        <button
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                            onClick={() => generatePassword(rules)}
                        >
                            {rules.passphrase ? "Generate Passphrase" : "Generate Custom Password"}
                        </button>
                    </div>
                </div>
            )}
        </div>

    );
}
